import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Lock, Unlock, Plus, Trash2, Edit3, Search, ShieldAlert, X, Check, GitBranch } from 'lucide-react';
import { useAuth } from '../context/AuthContext';
import { useProductCodes } from '../context/ProductCodesContext';

const API = process.env.REACT_APP_BACKEND_URL + '/api';
// Classes used to group Raw Materials in the Item Codes registry
const MATERIAL_CLASSES = [
  'Liquid', 'Powder', 'Acidulant', 'Base / Filler', 'Sweetener',
  'Spice', 'Herb', 'Colour', 'Flavour', 'Preservative', 'Oil',
  'Salt', 'Thickener', 'Emulsifier', 'Other',
];
const CATEGORIES = [
  { value: 'raw_material',  label: 'Raw Material' },
  { value: 'finished_good', label: 'Finished Good' },
];

export default function ProductCodes({ kind = 'item' }) {
  const { user } = useAuth();
  const isAdmin = user?.role === 'owner' || user?.role === 'admin';
  // Product Codes (client-facing) don't need the password gate — they're meant
  // to be public identifiers. Item Codes (secret RM aliases) still require it.
  const requiresUnlock = kind === 'item';
  const [unlocked, setUnlocked] = useState(!requiresUnlock);
  const [pw, setPw] = useState('');
  const [verifying, setVerifying] = useState(false);

  const [codes, setCodes] = useState([]);
  const [loading, setLoading] = useState(false);
  const [q, setQ] = useState('');

  // For the real-name typeahead (Item Code form): pull raw materials + FG
  const [rmFgOpts, setRmFgOpts] = useState([]);
  // Clients list — used when assigning a Product Code to specific clients
  const [clients, setClients] = useState([]);

  const [showModal, setShowModal] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState({
    code: '', real_name: '', base_code: '', variant_suffix: '',
    priority: 'general', category: kind === 'product' ? 'finished_good' : 'raw_material',
    unit: 'KG', notes: '', kind,
    material_class: '',
    client_ids: [],
  });

  // Filter the real-name datalist based on the mode: Item Code = RM only; Product Code = FG only
  const filteredRmFg = rmFgOpts.filter(o =>
    kind === 'product' ? o.category === 'finished_good' : o.category === 'raw_material'
  );

  const unlock = async (e) => {
    e?.preventDefault();
    if (!pw) return;
    setVerifying(true);
    try {
      await axios.post(`${API}/product-codes/verify-password`, { password: pw }, { withCredentials: true });
      setUnlocked(true);
      toast.success(`${kind === 'product' ? 'Product Codes' : 'Item Codes'} unlocked`);
      load();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Incorrect password');
    }
    finally { setVerifying(false); }
  };

  const load = async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API}/product-codes/?kind=${kind}`, { withCredentials: true });
      setCodes(res.data || []);
    } catch { /* silent */ }
    finally { setLoading(false); }
  };

  // Load RM + FG lists for the Item Code "Real Name" typeahead
  useEffect(() => {
    if (!isAdmin) return;
    (async () => {
      try {
        const [rm, fg, cl] = await Promise.all([
          axios.get(`${API}/inventory/raw-materials`, { withCredentials: true }),
          axios.get(`${API}/inventory/finished-goods`, { withCredentials: true }),
          axios.get(`${API}/clients`, { withCredentials: true }).catch(() => ({ data: [] })),
        ]);
        const opts = [
          ...((rm.data || []).map(m => ({ name: m.name, category: 'raw_material', unit: m.unit || 'kg' }))),
          ...((fg.data || []).map(m => ({ name: m.name, category: 'finished_good', unit: m.unit || 'kg' }))),
        ];
        setRmFgOpts(opts);
        setClients((cl.data || []).map(c => ({ id: c.id, name: c.name })));
      } catch { /* silent */ }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isAdmin]);

  const openNew = () => {
    setEditing(null);
    setForm({
      code: '', real_name: '', base_code: '', variant_suffix: '',
      priority: 'general',
      category: kind === 'product' ? 'finished_good' : 'raw_material',
      unit: 'KG', notes: '', kind,
      material_class: '',
      client_ids: [],
    });
    setShowModal(true);
  };
  const openEdit = (row) => {
    setEditing(row);
    setForm({
      code: row.code,
      real_name: row.real_name,
      base_code: row.base_code || '',
      variant_suffix: row.variant_suffix || '',
      priority: row.priority || 'general',
      category: row.category || (kind === 'product' ? 'finished_good' : 'raw_material'),
      unit: row.unit || 'KG',
      notes: row.notes || '',
      kind: row.kind || kind,
      material_class: row.material_class || '',
      client_ids: Array.isArray(row.client_ids) ? row.client_ids : [],
    });
    setShowModal(true);
  };

  // Open a new modal pre-populated as a *variant* of an existing code
  const cloneAsVariant = (row) => {
    setEditing(null);
    const base = row.base_code || row.code;
    setForm({
      code: `${base}-T2`,
      real_name: `${row.real_name} (variant)`,
      base_code: base,
      variant_suffix: 'T2',
      priority: 'general',
      category: row.category || 'raw_material',
      unit: row.unit || 'KG',
      notes: `Variant of ${row.code}`,
      kind,
    });
    setShowModal(true);
  };

  // Auto-load when the gate isn't required (Product Code view)
  useEffect(() => {
    if (!requiresUnlock) load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [kind]);

  const { refresh: refreshCodesMap } = useProductCodes();

  const submit = async (e) => {
    e.preventDefault();
    try {
      if (editing) {
        await axios.put(`${API}/product-codes/${editing.id}`, form, { withCredentials: true });
        toast.success('Code updated');
      } else {
        await axios.post(`${API}/product-codes/`, form, { withCredentials: true });
        toast.success('Code created');
      }
      setShowModal(false);
      load();
      refreshCodesMap();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Save failed');
    }
  };

  const remove = async (row) => {
    if (!window.confirm(`Delete mapping ${row.code} → ${row.real_name}?`)) return;
    try {
      await axios.delete(`${API}/product-codes/${row.id}`, { withCredentials: true });
      toast.success('Deleted');
      load();
      refreshCodesMap();
    } catch (err) { toast.error(err.response?.data?.detail || 'Delete failed'); }
  };

  const filtered = codes.filter(c => {
    if (!q) return true;
    const s = q.toLowerCase();
    return (c.code || '').toLowerCase().includes(s) || (c.real_name || '').toLowerCase().includes(s);
  });

  if (!unlocked) {
    return (
      <div className="flex items-center justify-center min-h-[60vh]">
        <form onSubmit={unlock} className="bg-zinc-900 border border-zinc-800 rounded-2xl p-6 w-full max-w-md space-y-4" data-testid="product-codes-gate">
          <div className="flex items-center gap-3 mb-2">
            <div className="w-10 h-10 rounded-xl bg-amber-500/15 border border-amber-500/30 flex items-center justify-center">
              <ShieldAlert size={18} className="text-amber-300" />
            </div>
            <div>
              <h2 className="font-heading font-semibold text-zinc-50 text-base">Item Codes — Locked</h2>
              <p className="text-zinc-500 text-xs">Enter the secondary password to view confidential RM/FG aliases.</p>
            </div>
          </div>
          <div>
            <label className="text-zinc-400 text-xs mb-1 block">Secondary Password</label>
            <input type="password" value={pw} onChange={e => setPw(e.target.value)} autoFocus
              data-testid="codes-password"
              placeholder="Secondary password"
              className="w-full bg-zinc-950 border border-zinc-700 rounded-lg px-3 py-2.5 text-zinc-50 text-sm focus:outline-none focus:border-amber-500" />
          </div>
          <button type="submit" disabled={verifying || !pw}
            data-testid="codes-unlock-btn"
            className="w-full flex items-center justify-center gap-2 bg-amber-500 hover:bg-amber-400 disabled:bg-zinc-700 disabled:text-zinc-400 text-black rounded-lg py-2.5 text-sm font-semibold transition-all">
            {verifying ? 'Verifying…' : <><Unlock size={14} /> Unlock</>}
          </button>
          <p className="text-[10px] text-zinc-600 text-center">
            All access attempts are logged. Admins are notified on every unlock.
          </p>
        </form>
      </div>
    );
  }

  return (
    <div className="space-y-4" data-testid="product-codes-page">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h1 className="font-heading text-2xl font-semibold text-zinc-50 flex items-center gap-2">
            {kind === 'product' ? null : <Unlock size={18} className="text-green-400" />}
            {kind === 'product' ? 'Product Codes' : 'Item Codes'}
          </h1>
          <p className="text-zinc-500 text-xs mt-0.5">
            {kind === 'product'
              ? `Client-facing recipe variant IDs · linked to recipes & dispatch · ${codes.length} ${codes.length === 1 ? 'code' : 'codes'}`
              : `Confidential RM/FG alias map · ${codes.length} ${codes.length === 1 ? 'code' : 'codes'}`}
          </p>
        </div>
        <div className="flex items-center gap-2">
          <div className="relative">
            <Search size={12} className="absolute left-2.5 top-1/2 -translate-y-1/2 text-zinc-500" />
            <input value={q} onChange={e => setQ(e.target.value)} placeholder="Search code or real name…"
              data-testid="codes-search"
              className="bg-zinc-900 border border-zinc-800 rounded-lg pl-7 pr-3 py-2 text-xs text-zinc-50 w-64 focus:outline-none focus:border-amber-500" />
          </div>
          <button onClick={openNew} data-testid="new-code-btn"
            className="flex items-center gap-2 px-4 py-2 bg-amber-500 hover:bg-amber-400 text-black rounded-lg text-xs font-semibold transition-all">
            <Plus size={14} /> New Code
          </button>
          {requiresUnlock && (
            <button onClick={() => { setUnlocked(false); setPw(''); }} data-testid="relock-btn"
              className="flex items-center gap-2 px-3 py-2 bg-zinc-900 border border-zinc-700 rounded-lg text-zinc-400 hover:text-zinc-200 text-xs">
              <Lock size={14} /> Re-lock
            </button>
          )}
        </div>
      </div>

      <div className="bg-zinc-900 border border-zinc-800 rounded-xl overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-sm min-w-[900px]" data-testid="codes-table">
            <thead>
              <tr className="border-b border-zinc-800 bg-zinc-950/50">
                {['Code', 'Base / Variant', 'Real Name', 'Priority', 'Category', 'Unit', 'Notes', 'Actions'].map(h => (
                  <th key={h} className="text-left text-zinc-500 text-[11px] font-medium px-4 py-2.5 uppercase tracking-wider whitespace-nowrap">{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {loading && <tr><td colSpan={8} className="p-6 text-center text-zinc-500 text-sm">Loading…</td></tr>}
              {!loading && filtered.length === 0 && (
                <tr><td colSpan={8} className="p-10 text-center text-zinc-500 text-sm">
                  {codes.length === 0 ? 'No codes yet. Click "New Code" to create your first alias.' : 'No matches'}
                </td></tr>
              )}
              {filtered.map(c => {
                const isVariant = !!c.variant_suffix;
                const priority = c.priority || 'general';
                const pstyle = priority === 'high'
                  ? 'bg-red-900/30 text-red-300 border-red-700'
                  : priority === 'low'
                  ? 'bg-zinc-800 text-zinc-400 border-zinc-700'
                  : 'bg-zinc-800 text-zinc-300 border-zinc-700';
                return (
                  <tr key={c.id} className="border-b border-zinc-800/50 hover:bg-zinc-800/30" data-testid={`code-row-${c.id}`}>
                    <td className="px-4 py-2.5 font-mono text-amber-300 text-xs font-semibold whitespace-nowrap">
                      {isVariant && <span className="text-zinc-600 mr-1">└</span>}
                      {c.code}
                    </td>
                    <td className="px-4 py-2.5 text-zinc-400 text-[11px] whitespace-nowrap">
                      {c.base_code || '—'}
                      {c.variant_suffix && <span className="ml-1 px-1.5 py-0.5 rounded bg-sky-500/15 text-sky-300 text-[10px] font-semibold">{c.variant_suffix}</span>}
                    </td>
                    <td className="px-4 py-2.5 text-zinc-100 text-xs">{c.real_name}</td>
                    <td className="px-4 py-2.5 whitespace-nowrap">
                      <span className={`text-[10px] px-2 py-0.5 rounded-full border font-semibold ${pstyle}`}>{priority}</span>
                    </td>
                    <td className="px-4 py-2.5 text-zinc-400 text-xs whitespace-nowrap">
                      <span className={`px-2 py-0.5 rounded-full text-[10px] ${c.category === 'finished_good' ? 'bg-green-500/15 text-green-300' : 'bg-blue-500/15 text-blue-300'}`}>
                        {c.category === 'finished_good' ? 'FG' : 'RM'}
                      </span>
                    </td>
                    <td className="px-4 py-2.5 text-zinc-400 text-xs font-mono whitespace-nowrap">{c.unit}</td>
                    <td className="px-4 py-2.5 text-zinc-500 text-xs max-w-xs truncate" title={c.notes}>{c.notes || '—'}</td>
                    <td className="px-4 py-2.5 whitespace-nowrap">
                      <div className="flex items-center gap-1">
                        <button onClick={() => cloneAsVariant(c)} data-testid={`clone-variant-${c.id}`}
                          className="p-1.5 rounded bg-sky-500/15 text-sky-300 hover:bg-sky-500/25" title="Create a variant (e.g. T2) of this product">
                          <GitBranch size={11} />
                        </button>
                        <button onClick={() => openEdit(c)} data-testid={`edit-${c.id}`}
                          className="p-1.5 rounded bg-zinc-800 text-zinc-300 hover:bg-zinc-700">
                          <Edit3 size={11} />
                        </button>
                        {isAdmin && (
                          <button onClick={() => remove(c)} data-testid={`delete-${c.id}`}
                            className="p-1.5 rounded bg-red-900/30 text-red-400 hover:text-red-300">
                            <Trash2 size={11} />
                          </button>
                        )}
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>

      {showModal && (
        <div className="fixed inset-0 bg-black/70 flex items-center justify-center z-50 p-4" onClick={() => setShowModal(false)}>
          <form onSubmit={submit} className="bg-zinc-950 border border-zinc-800 rounded-2xl w-full max-w-md" onClick={e => e.stopPropagation()}>
            <div className="flex items-center justify-between px-5 py-3 border-b border-zinc-800">
              <h3 className="font-heading font-semibold text-zinc-50 text-sm">
                {editing ? 'Edit Product Code' : 'New Product Code'}
              </h3>
              <button type="button" onClick={() => setShowModal(false)} className="text-zinc-500 hover:text-zinc-200">
                <X size={16} />
              </button>
            </div>
            <div className="p-5 space-y-3">
              <div>
                <label className="text-zinc-400 text-xs mb-1 block">Code (Alias) *</label>
                <input required value={form.code} onChange={e => setForm(f => ({ ...f, code: e.target.value }))}
                  data-testid="form-code" placeholder="e.g. L 100"
                  className="w-full bg-zinc-900 border border-zinc-700 rounded-lg px-3 py-2 text-zinc-50 text-sm focus:outline-none focus:border-amber-500 font-mono" />
              </div>
              <div>
                <label className="text-zinc-400 text-xs mb-1 block">Real Name * <span className="text-zinc-600">({kind === 'product' ? 'recipe product' : 'pick from RM / FG'})</span></label>
                <input
                  required
                  value={form.real_name}
                  onChange={e => {
                    const v = e.target.value;
                    // Auto-set category + unit when the typed name matches an RM/FG
                    const match = rmFgOpts.find(o => o.name.toLowerCase() === v.toLowerCase());
                    setForm(f => ({
                      ...f,
                      real_name: v,
                      ...(match ? { category: match.category, unit: (match.unit || f.unit || 'KG').toUpperCase() } : {}),
                    }));
                  }}
                  list="pc-real-name-list"
                  data-testid="form-real-name"
                  placeholder={kind === 'product' ? 'e.g. Peri Peri Premium' : 'Start typing a material or FG…'}
                  className="w-full bg-zinc-900 border border-zinc-700 rounded-lg px-3 py-2 text-zinc-50 text-sm focus:outline-none focus:border-amber-500"
                  autoComplete="off"
                />
                <datalist id="pc-real-name-list">
                  {filteredRmFg.map(o => (
                    <option key={o.name} value={o.name}>
                      {o.category === 'finished_good' ? 'FG' : 'RM'} · {o.unit}
                    </option>
                  ))}
                </datalist>
              </div>
              <div className="grid grid-cols-2 gap-3">
                {kind === 'product' && (
                  <>
                    <div>
                      <label className="text-zinc-400 text-xs mb-1 block">Base Code <span className="text-zinc-600">(family)</span></label>
                      <input value={form.base_code} onChange={e => setForm(f => ({ ...f, base_code: e.target.value }))}
                        data-testid="form-base-code" placeholder="TI-151"
                        className="w-full bg-zinc-900 border border-zinc-700 rounded-lg px-3 py-2 text-zinc-50 text-sm focus:outline-none focus:border-amber-500 font-mono" />
                      <p className="text-[10px] text-zinc-600 mt-1">Same family = same class (e.g. TI-151 = peri peri)</p>
                    </div>
                    <div>
                      <label className="text-zinc-400 text-xs mb-1 block">Variant <span className="text-zinc-600">(e.g. T1/T2)</span></label>
                      <input value={form.variant_suffix} onChange={e => setForm(f => ({ ...f, variant_suffix: e.target.value }))}
                        data-testid="form-variant-suffix" placeholder="T2"
                        className="w-full bg-zinc-900 border border-zinc-700 rounded-lg px-3 py-2 text-zinc-50 text-sm focus:outline-none focus:border-amber-500 font-mono" />
                      <p className="text-[10px] text-zinc-600 mt-1">Different recipe under same base</p>
                    </div>
                  </>
                )}
                {kind === 'item' && (
                  <div className="col-span-2">
                    <label className="text-zinc-400 text-xs mb-1 block">Material Class *</label>
                    <select value={form.material_class || ''} onChange={e => setForm(f => ({ ...f, material_class: e.target.value }))}
                      data-testid="form-material-class"
                      className="w-full bg-zinc-900 border border-zinc-700 rounded-lg px-3 py-2 text-zinc-50 text-sm focus:outline-none focus:border-amber-500">
                      <option value="">— Select class —</option>
                      {MATERIAL_CLASSES.map(c => <option key={c} value={c} className="bg-zinc-950">{c}</option>)}
                    </select>
                    <p className="text-[10px] text-zinc-600 mt-1">Physical/functional grouping of the raw material</p>
                  </div>
                )}
              </div>
              <div>
                <label className="text-zinc-400 text-xs mb-1 block">Priority *</label>
                <div className="grid grid-cols-3 gap-2">
                  {['high', 'general', 'low'].map(p => (
                    <button type="button" key={p}
                      onClick={() => setForm(f => ({ ...f, priority: p }))}
                      data-testid={`form-priority-${p}`}
                      className={`py-2 rounded-lg text-xs font-semibold transition-all ${
                        form.priority === p
                          ? (p === 'high' ? 'bg-red-600 text-white'
                             : p === 'low' ? 'bg-zinc-700 text-zinc-200'
                             : 'bg-amber-500 text-black')
                          : 'bg-zinc-800 text-zinc-500 hover:text-zinc-200'
                      }`}>
                      {p.toUpperCase()}
                    </button>
                  ))}
                </div>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="text-zinc-400 text-xs mb-1 block">Category <span className="text-zinc-600">(locked by tab)</span></label>
                  <select value={form.category} disabled onChange={e => setForm(f => ({ ...f, category: e.target.value }))}
                    data-testid="form-category"
                    className="w-full bg-zinc-900 border border-zinc-700 rounded-lg px-3 py-2 text-zinc-500 text-sm focus:outline-none cursor-not-allowed">
                    {CATEGORIES.map(c => <option key={c.value} value={c.value}>{c.label}</option>)}
                  </select>
                </div>
                <div>
                  <label className="text-zinc-400 text-xs mb-1 block">Unit</label>
                  <input value={form.unit} onChange={e => setForm(f => ({ ...f, unit: e.target.value }))}
                    data-testid="form-unit"
                    className="w-full bg-zinc-900 border border-zinc-700 rounded-lg px-3 py-2 text-zinc-50 text-sm focus:outline-none focus:border-amber-500 font-mono" />
                </div>
              </div>
              {kind === 'product' && (
                <div>
                  <label className="text-zinc-400 text-xs mb-1 block">
                    Assigned Clients <span className="text-zinc-600">(optional · empty = visible to all clients)</span>
                  </label>
                  <select multiple size={Math.min(6, clients.length + 1)}
                    value={form.client_ids}
                    onChange={e => setForm(f => ({ ...f, client_ids: Array.from(e.target.selectedOptions, o => o.value) }))}
                    data-testid="form-client-ids"
                    className="w-full bg-zinc-900 border border-zinc-700 rounded-lg px-3 py-2 text-zinc-50 text-xs focus:outline-none focus:border-amber-500">
                    {clients.map(c => <option key={c.id} value={c.id}>{c.name}</option>)}
                  </select>
                  <p className="text-[10px] text-zinc-600 mt-1">Hold Ctrl/Cmd to pick multiple. Clients see only the codes assigned to them in their portal.</p>
                </div>
              )}
              <div>
                <label className="text-zinc-400 text-xs mb-1 block">Notes</label>
                <textarea rows={2} value={form.notes} onChange={e => setForm(f => ({ ...f, notes: e.target.value }))}
                  data-testid="form-notes" placeholder="Optional"
                  className="w-full bg-zinc-900 border border-zinc-700 rounded-lg px-3 py-2 text-zinc-50 text-sm focus:outline-none focus:border-amber-500" />
              </div>
              <div className="flex justify-end gap-2 pt-2">
                <button type="button" onClick={() => setShowModal(false)}
                  className="px-4 py-2 bg-zinc-800 text-zinc-300 rounded-lg text-xs">
                  Cancel
                </button>
                <button type="submit" data-testid="form-save"
                  className="flex items-center gap-1.5 px-4 py-2 bg-amber-500 hover:bg-amber-400 text-black rounded-lg text-xs font-semibold">
                  <Check size={13} /> {editing ? 'Save Changes' : 'Create'}
                </button>
              </div>
            </div>
          </form>
        </div>
      )}
    </div>
  );
}
